import React, { useState, useRef, useEffect } from 'react';
import { getSummary, processWithLLM, uploadAudio } from '../services/api';
import { anonymizeTranscript } from '../utils/anonymize';
import './AudioRecorder.css';

// Minimum average frequency magnitude (0–255) considered "speech"
const SILENCE_THRESHOLD = 18;
// How long silence must last before flushing the current audio segment
const SILENCE_DURATION_MS = 700;
// Maximum segment length before forced flush — keeps Whisper chunks short enough
// for ~2-3s processing time instead of 6-7s for longer sentences
const MAX_SEGMENT_MS = 8000;
// Minimum blob size to bother sending (avoids sending empty clicks)
const MIN_SEGMENT_BYTES = 800;

function AudioRecorder({
  onTranscription, onSummary, onQuestions, onLLMResult,
  isProcessing, setIsProcessing,
  setSummaryUpdating, setQuestionsUpdating, setAnalysisUpdating,
  manualNotes, numDiagnoses,
  onSessionStart, onSessionPause, onSessionComplete,
  resumeSessionName, initialTranscript,
  onSegmentTranscribed,
  existingPatients, onRequestPatients, patientContext,
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalInput, setModalInput] = useState('');
  const [patientInput, setPatientInput] = useState('');
  const modalInputRef = useRef(null);
  const patientInputRef = useRef(null);
  const patientContextRef = useRef(patientContext);

  // Audio infrastructure
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceIntervalRef = useRef(null);
  const isFlushingRef = useRef(false);

  // Transcript accumulation
  const accumulatedTranscriptRef = useRef('');
  const liveTranscriptRef = useRef('');

  // Summary / analysis state
  const summaryTimeoutRef = useRef(null);
  const notesTimeoutRef = useRef(null);
  const analysisTimeoutRef = useRef(null);
  const onSummaryRef = useRef(onSummary);
  const onTranscriptionRef = useRef(onTranscription);
  const onQuestionsRef = useRef(onQuestions);
  const onLLMResultRef = useRef(onLLMResult);
  const onSegmentTranscribedRef = useRef(onSegmentTranscribed);
  const manualNotesRef = useRef(manualNotes);
  const numDiagnosesRef = useRef(numDiagnoses);
  const isSummarizingRef = useRef(false);
  const isAnalyzingRef = useRef(false);
  const pendingSummaryTextRef = useRef(null);
  const pendingAnalysisTextRef = useRef(null);
  const lastSummarizedTextRef = useRef('');
  const lastAnalysisTextRef = useRef('');
  const currentSummaryRef = useRef(null);
  const isRecordingRef = useRef(false);
  const hasSpeechInSegmentRef = useRef(false);
  const sessionIdRef = useRef(null);

  // Displayed transcript (drives summary/analysis effects)
  const [finalTranscript, setFinalTranscript] = useState('');

  // ── Resume session ──────────────────────────────────────────────
  useEffect(() => {
    if (resumeSessionName && !sessionActive) {
      setSessionName(resumeSessionName);
      setSessionActive(true);
      if (initialTranscript) {
        setFinalTranscript(initialTranscript);
        liveTranscriptRef.current = initialTranscript;
        accumulatedTranscriptRef.current = initialTranscript;
      }
    }
  }, [resumeSessionName]);

  // ── Keep callback refs current ──────────────────────────────────
  useEffect(() => { onSummaryRef.current = onSummary; }, [onSummary]);
  useEffect(() => { onTranscriptionRef.current = onTranscription; }, [onTranscription]);
  useEffect(() => { onQuestionsRef.current = onQuestions; }, [onQuestions]);
  useEffect(() => { onLLMResultRef.current = onLLMResult; }, [onLLMResult]);
  useEffect(() => { onSegmentTranscribedRef.current = onSegmentTranscribed; }, [onSegmentTranscribed]);
  useEffect(() => { manualNotesRef.current = manualNotes; }, [manualNotes]);
  useEffect(() => { numDiagnosesRef.current = numDiagnoses; }, [numDiagnoses]);
  useEffect(() => { patientContextRef.current = patientContext; }, [patientContext]);

  // ── Real-time summary while recording ──────────────────────────
  useEffect(() => {
    if (!isRecording || !finalTranscript || finalTranscript.length < 20) return;
    // Nicht feuern wenn der Transcript noch vom vorherigen Resume stammt
    // (accumulatedTranscriptRef ist leer solange noch nichts aufgenommen wurde)
    if (!accumulatedTranscriptRef.current) return;
    if (finalTranscript === lastSummarizedTextRef.current) return;
    if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current);

    summaryTimeoutRef.current = setTimeout(async () => {
      const text = buildSummaryText(finalTranscript);
      if (text === lastSummarizedTextRef.current) return;
      if (isSummarizingRef.current) { pendingSummaryTextRef.current = text; return; }
      isSummarizingRef.current = true;
      setSummaryUpdating(true);
      try {
        const summary = await getSummary(text, null, patientContextRef.current);
        console.log('[Summary]', summary);
        const hadContent = currentSummaryRef.current && currentSummaryRef.current.length > 0;
        if (!hadContent || (summary && summary.length > 0)) {
          currentSummaryRef.current = summary || [];
          onSummaryRef.current(summary || []);
          lastSummarizedTextRef.current = text;
        }
      } catch (e) {
        console.error('[Summary error]', e);
      } finally {
        isSummarizingRef.current = false;
        setSummaryUpdating(false);
        const pending = pendingSummaryTextRef.current;
        if (pending && pending !== lastSummarizedTextRef.current && isRecordingRef.current) {
          pendingSummaryTextRef.current = null;
          runPendingSummary(pending);
        }
      }
    }, 400);
    return () => { if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current); };
  }, [finalTranscript, isRecording]);

  async function runPendingSummary(text) {
    if (isSummarizingRef.current) return;
    isSummarizingRef.current = true;
    setSummaryUpdating(true);
    try {
      const summary = await getSummary(text, null);
      const hadContent = currentSummaryRef.current && currentSummaryRef.current.length > 0;
      if (!hadContent || (summary && summary.length > 0)) {
        currentSummaryRef.current = summary || [];
        onSummaryRef.current(summary || []);
        lastSummarizedTextRef.current = text;
      }
    } catch (e) {
      console.error('[Pending summary error]', e);
    } finally {
      isSummarizingRef.current = false;
      setSummaryUpdating(false);
    }
  }

  // ── Re-trigger summary when manual notes change ─────────────────
  useEffect(() => {
    const transcript = finalTranscript || liveTranscriptRef.current;
    if (!transcript || transcript.length < 20 || !manualNotes) return;
    if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
    notesTimeoutRef.current = setTimeout(async () => {
      const text = buildSummaryText(transcript);
      if (text === lastSummarizedTextRef.current || isSummarizingRef.current) return;
      isSummarizingRef.current = true;
      setSummaryUpdating(true);
      try {
        const summary = await getSummary(text, null, patientContextRef.current);
        const hadContent = currentSummaryRef.current && currentSummaryRef.current.length > 0;
        if (!hadContent || (summary && summary.length > 0)) {
          currentSummaryRef.current = summary || [];
          onSummaryRef.current(summary || []);
          lastSummarizedTextRef.current = text;
        }
      } catch (e) {
        console.error('[Notes summary error]', e);
      } finally {
        isSummarizingRef.current = false;
        setSummaryUpdating(false);
      }
    }, 400);
    return () => { if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current); };
  }, [manualNotes]);

  // ── Real-time differential diagnosis ───────────────────────────
  useEffect(() => {
    if (!isRecording || !finalTranscript || finalTranscript.length < 60) return;
    const newLen = finalTranscript.length - lastAnalysisTextRef.current.length;
    if (newLen < 150 && lastAnalysisTextRef.current.length > 0) return;
    if (analysisTimeoutRef.current) clearTimeout(analysisTimeoutRef.current);

    analysisTimeoutRef.current = setTimeout(async () => {
      const text = finalTranscript;
      const delta = text.length - lastAnalysisTextRef.current.length;
      if (delta < 150 && lastAnalysisTextRef.current.length > 0) return;
      if (isAnalyzingRef.current) { pendingAnalysisTextRef.current = text; return; }
      isAnalyzingRef.current = true;
      setAnalysisUpdating(true);
      setQuestionsUpdating(true);
      try {
        const summaryJson = currentSummaryRef.current ? JSON.stringify(currentSummaryRef.current) : null;
        const diagnoses = await processWithLLM(text, null, numDiagnosesRef.current, summaryJson, patientContextRef.current);
        onLLMResultRef.current(diagnoses);
        const questions = extractQuestionsFromDiagnoses(diagnoses);
        if (questions) onQuestionsRef.current(questions);
        lastAnalysisTextRef.current = text;
      } catch (e) {
        console.error('Analysis error:', e);
      } finally {
        isAnalyzingRef.current = false;
        setAnalysisUpdating(false);
        setQuestionsUpdating(false);
        const pending = pendingAnalysisTextRef.current;
        if (pending && isRecordingRef.current && pending.length - lastAnalysisTextRef.current.length >= 150) {
          pendingAnalysisTextRef.current = null;
          runPendingAnalysis(pending);
        }
      }
    }, 400);
    return () => { if (analysisTimeoutRef.current) clearTimeout(analysisTimeoutRef.current); };
  }, [finalTranscript, isRecording]);

  async function runPendingAnalysis(text) {
    if (isAnalyzingRef.current) return;
    isAnalyzingRef.current = true;
    setAnalysisUpdating(true);
    setQuestionsUpdating(true);
    try {
      const summaryJson = currentSummaryRef.current ? JSON.stringify(currentSummaryRef.current) : null;
      const diagnoses = await processWithLLM(text, null, numDiagnosesRef.current, summaryJson);
      onLLMResultRef.current(diagnoses);
      const questions = extractQuestionsFromDiagnoses(diagnoses);
      if (questions) onQuestionsRef.current(questions);
      lastAnalysisTextRef.current = text;
    } catch (e) {
      console.error('Pending analysis error:', e);
    } finally {
      isAnalyzingRef.current = false;
      setAnalysisUpdating(false);
      setQuestionsUpdating(false);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────
  const buildSummaryText = (transcript) => {
    const notes = manualNotesRef.current;
    return notes?.trim()
      ? `${transcript}\n\nErgänzungen des Arztes:\n${notes.trim()}`
      : transcript;
  };

  const extractQuestionsFromDiagnoses = (diagnoses) => {
    if (!diagnoses || !Array.isArray(diagnoses)) return null;
    return diagnoses
      .filter(d => d.questions && d.questions.length > 0)
      .map(d => ({ diagnosis: d.name, questions: d.questions }));
  };

  // ── Audio recording internals ───────────────────────────────────

  const startMediaRecorder = (stream) => {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : '';
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    mediaRecorderRef.current = recorder;
    audioChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    recorder.start();
  };

  const stopCurrentSegment = () =>
    new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        const chunks = [...audioChunksRef.current];
        audioChunksRef.current = [];
        resolve(chunks);
        return;
      }
      recorder.onstop = () => {
        const chunks = [...audioChunksRef.current];
        audioChunksRef.current = [];
        resolve(chunks);
      };
      recorder.stop();
    });

  const flushSegment = async () => {
    if (isFlushingRef.current) return;
    isFlushingRef.current = true;
    hasSpeechInSegmentRef.current = false;

    const chunks = await stopCurrentSegment();

    // Start the next recording segment immediately — don't wait for Whisper/LLM
    if (isRecordingRef.current && streamRef.current) startMediaRecorder(streamRef.current);

    if (chunks.length === 0) {
      isFlushingRef.current = false;
      return;
    }

    const blob = new Blob(chunks, { type: 'audio/webm' });
    if (blob.size < MIN_SEGMENT_BYTES) {
      isFlushingRef.current = false;
      return;
    }

    try {
      const { text, speaker } = await uploadAudio(blob, sessionIdRef.current);
      if (text && text.trim()) {
        const trimmed = text.trim();
        const sep = accumulatedTranscriptRef.current ? ' ' : '';
        const combined = accumulatedTranscriptRef.current + sep + trimmed;
        const anonymized = anonymizeTranscript(combined);
        accumulatedTranscriptRef.current = anonymized;
        liveTranscriptRef.current = anonymized;
        setFinalTranscript(anonymized);
        onTranscriptionRef.current(anonymized);
        if (onSegmentTranscribedRef.current) {
          onSegmentTranscribedRef.current(trimmed, Date.now(), speaker);
        }
      }
    } catch (e) {
      console.error('Segment transcription error:', e);
    } finally {
      isFlushingRef.current = false;
    }
  };

  const startSilenceDetection = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let silenceMs = 0;
    let segmentMs = 0;

    silenceIntervalRef.current = setInterval(() => {
      if (!isRecordingRef.current || !analyserRef.current) return;
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
      if (avg >= SILENCE_THRESHOLD) {
        hasSpeechInSegmentRef.current = true;
        silenceMs = 0;
      } else {
        silenceMs += 100;
        if (silenceMs >= SILENCE_DURATION_MS && hasSpeechInSegmentRef.current) {
          silenceMs = 0;
          segmentMs = 0;
          flushSegment();
          return;
        }
      }
      segmentMs += 100;
      if (hasSpeechInSegmentRef.current && segmentMs >= MAX_SEGMENT_MS) {
        silenceMs = 0;
        segmentMs = 0;
        flushSegment();
      }
    }, 100);
  };

  const stopSilenceDetection = () => {
    if (silenceIntervalRef.current) {
      clearInterval(silenceIntervalRef.current);
      silenceIntervalRef.current = null;
    }
  };

  // ── Recording controls ──────────────────────────────────────────

  const startRecording = async () => {
    try {
      setFinalTranscript('');
      onTranscriptionRef.current('');
      onSummary(null);
      onQuestionsRef.current(null);
      onLLMResult(null);
      lastSummarizedTextRef.current = '';
      liveTranscriptRef.current = '';
      accumulatedTranscriptRef.current = '';
      isSummarizingRef.current = false;
      isAnalyzingRef.current = false;
      isFlushingRef.current = false;
      hasSpeechInSegmentRef.current = false;
      sessionIdRef.current = `session_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      pendingSummaryTextRef.current = null;
      pendingAnalysisTextRef.current = null;
      lastAnalysisTextRef.current = '';
      currentSummaryRef.current = null;
      if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current);
      if (analysisTimeoutRef.current) clearTimeout(analysisTimeoutRef.current);
      if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
      stopSilenceDetection();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      startMediaRecorder(stream);
      startSilenceDetection();

      setIsRecording(true);
      setIsPaused(false);
      isRecordingRef.current = true;
    } catch (e) {
      console.error('Microphone error:', e);
      alert('Fehler beim Zugriff auf das Mikrofon: ' + e.message);
    }
  };

  const pauseRecording = async () => {
    if (!isRecording || isPaused) return;
    isRecordingRef.current = false;
    stopSilenceDetection();
    await flushSegment();
    setIsPaused(true);
  };

  const resumeRecording = () => {
    if (!isRecording || !isPaused) return;
    if (streamRef.current) startMediaRecorder(streamRef.current);
    isRecordingRef.current = true;
    startSilenceDetection();
    setIsPaused(false);
  };

  const stopRecording = async () => {
    if (!isRecording) return;
    isRecordingRef.current = false;
    stopSilenceDetection();
    await flushSegment();
    if (summaryTimeoutRef.current) clearTimeout(summaryTimeoutRef.current);
    if (analysisTimeoutRef.current) clearTimeout(analysisTimeoutRef.current);
    if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
    pendingSummaryTextRef.current = null;
    pendingAnalysisTextRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setIsRecording(false);
    setIsPaused(false);
  };

  // ── Session controls ────────────────────────────────────────────

  const generateDefaultSessionName = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `Konsultation - ${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const handleStartSession = () => {
    setModalInput(generateDefaultSessionName());
    setPatientInput('');
    setShowModal(true);
    if (onRequestPatients) onRequestPatients();
    setTimeout(() => modalInputRef.current?.select(), 50);
  };

  const confirmSession = () => {
    const name = modalInput.trim() || generateDefaultSessionName();
    const pid = patientInput.trim();
    setSessionName(name);
    setSessionActive(true);
    setShowModal(false);
    if (onSessionStart) onSessionStart(name, pid);
  };

  const handlePauseSession = async () => {
    if (isRecording || isPaused) await stopRecording();
    const name = sessionName;
    setSessionActive(false);
    setSessionName('');
    if (onSessionPause) onSessionPause(name);
  };

  const handleCompleteSession = async () => {
    if (isRecording || isPaused) await stopRecording();
    const name = sessionName;
    if (onSessionComplete) onSessionComplete(name);
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); confirmSession(); }
  };

  // ── Icons ───────────────────────────────────────────────────────
  const PauseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
  );
  const StopIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="6" width="12" height="12" rx="1" />
    </svg>
  );
  const SessionStartIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6" /><path d="M10 14L21 3" /><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    </svg>
  );
  const SessionPauseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
    </svg>
  );
  const SessionCompleteIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
  const MicIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );

  const cardClass = `session-card${isRecording && !isPaused ? ' recording' : ''}`;

  return (
    <>
      <div className={cardClass}>
        <h2>Gesprächsführung</h2>

        {sessionActive && sessionName && (
          <div className="session-info">
            <span className="session-name-badge">{sessionName}</span>
            {isRecording && !isPaused && (
              <span className="recording-indicator">
                <span className="recording-dot" />
                Aufnahme läuft...
              </span>
            )}
            {isRecording && isPaused && (
              <span className="recording-indicator paused-indicator">
                <span className="recording-dot" />
                Pausiert
              </span>
            )}
          </div>
        )}

        <div className="session-controls">
          <button
            className="session-btn session-btn-start"
            onClick={handleStartSession}
            disabled={sessionActive || isProcessing}
          >
            <SessionStartIcon />
            Konsultation starten
          </button>

          <div className="recording-btn-group">
            {!isRecording ? (
              <button
                className="rec-btn rec-btn-record"
                onClick={startRecording}
                disabled={!sessionActive || isProcessing}
              >
                <MicIcon />
                Aufnahme starten
              </button>
            ) : (
              <button
                className="rec-btn rec-btn-record"
                onClick={resumeRecording}
                disabled={!isPaused}
              >
                <MicIcon />
                Fortsetzen
              </button>
            )}
            <button
              className="rec-btn rec-btn-pause"
              onClick={pauseRecording}
              disabled={!isRecording || isPaused}
            >
              <PauseIcon />
              Pausieren
            </button>
            <button
              className="rec-btn rec-btn-stop"
              onClick={stopRecording}
              disabled={!isRecording}
            >
              <StopIcon />
              Beenden
            </button>
          </div>

          <div className="session-end-group">
            <button
              className="session-btn session-btn-pause-session"
              onClick={handlePauseSession}
              disabled={!sessionActive || isProcessing}
            >
              <SessionPauseIcon />
              Konsultation unterbrechen
            </button>
            <button
              className="session-btn session-btn-end"
              onClick={handleCompleteSession}
              disabled={!sessionActive || isProcessing}
            >
              <SessionCompleteIcon />
              Konsultation abschließen
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="session-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="session-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Neue Konsultation</h3>
            <input
              ref={modalInputRef}
              className="session-modal-input"
              type="text"
              value={modalInput}
              onChange={(e) => setModalInput(e.target.value)}
              onKeyDown={handleModalKeyDown}
              placeholder="Name eingeben..."
              autoFocus
            />
            <label style={{ display: 'block', fontSize: '13px', color: '#6b7280', marginTop: '12px', marginBottom: '4px' }}>
              Patienten-ID <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              ref={patientInputRef}
              className="session-modal-input"
              type="text"
              list="patient-suggestions"
              value={patientInput}
              onChange={(e) => setPatientInput(e.target.value)}
              onKeyDown={handleModalKeyDown}
              placeholder="z.B. P-001 oder eigenes Kürzel"
            />
            {existingPatients && existingPatients.length > 0 && (
              <datalist id="patient-suggestions">
                {existingPatients.map((p) => (
                  <option key={p.id} value={p.patient_label} />
                ))}
              </datalist>
            )}
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', lineHeight: '1.5' }}>
              In der Produktivversion wird diese ID automatisch als anonymer Hash generiert —
              z.&nbsp;B. aus der Krankenversicherungskarte. Caiser Medical speichert ausschließlich
              diesen Hash und kann ihn keinem Patienten zuordnen. Die Auflösung erfolgt
              ausschließlich lokal in der App des Arztes und in der App des Patienten. Somit sind persönliche
              Daten der Patient:innen immer geschützt.
            </p>
            <div className="session-modal-actions">
              <button className="modal-btn-cancel" onClick={() => setShowModal(false)}>
                Abbrechen
              </button>
              <button className="modal-btn-confirm" onClick={confirmSession}>
                Fertig
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AudioRecorder;
